import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { Close, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';

import { CountryInfo, countries as countryList } from '@frontend/features/common/constants/countries';
import { updateSearchParams } from '@frontend/features/common/helper';

import { useHotelMetadata } from '../../hooks';
import { BoxMinWidth, FilterTypes, FilterUrlSplitter, ModStatus } from './constants';
import { MultiTextSelect } from './MultiTextSelect';
import { useGetAvailableCountries } from '@frontend/features/hotel-content-manager/queries';

const StyledBoxHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid var(--divider, ${theme.palette.grey[400]})`,
}));

const StyledBoxFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
  marginTop: theme.spacing(3),
  justifyContent: 'flex-end',
  borderTop: `1px solid var(--divider, ${theme.palette.grey[400]})`,
}));

interface Props {
  onClose: () => void;
  onClearFilters: () => void;
}

export const Filters: FC<Props> = ({ onClose, onClearFilters}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {isLoading: loadingMeta, suppliers } = useHotelMetadata();
  const { data: availableCountries, isLoading: loadingCountries } = useGetAvailableCountries();

  const countries = availableCountries?.map((country) => countryList.find((c) => c.code === country) as CountryInfo);

  return (
    <Box
      flexDirection="column"
      display="flex"
      width="400px"
    >
      {
        (loadingMeta || loadingCountries) ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <StyledBoxHeader>
              <Typography variant="subtitle2" gutterBottom={false}>
                {t('common.filters')}
              </Typography>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </StyledBoxHeader>

            <Box
              display="flex"
              flexDirection="column"
              mt={3}
              px={3}
              gap={3}
            >   
              
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  fullWidth
                  limitTags={1}
                  size="small"
                  options={suppliers || []}
                  getOptionLabel={(option) => option.accessName}
                  value={
                    suppliers?.filter((option) => searchParams
                      .get(FilterTypes.supplier)?.split(FilterUrlSplitter)?.includes(String(option.id))) || []
                  }
                  onChange={(_event, value) => {
                    const valuesForUrl = value.map((val) => val.id).join(FilterUrlSplitter);
                    updateSearchParams(searchParams, setSearchParams, { [FilterTypes.supplier]: valuesForUrl, [FilterTypes.page]: '1' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      fullWidth
                      placeholder={t('common.fields.provider')}
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const { ...optionProps } = props;
                    return (
                      <li {...optionProps} key={option.id}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlank fontSize="small" />}
                          checkedIcon={<CheckBox fontSize="small" />}
                          sx={{ mr: 1 }}
                          checked={selected}
                        />
                        {option.supplierName}
                      </li>
                    );
                  }}
                />

              <Box minWidth={BoxMinWidth}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  fullWidth
                  limitTags={1}
                  size="small"
                  options={countries || []}
                  getOptionLabel={(option) => option.name}
                  value={
                    countries?.filter((option) => searchParams
                      .get(FilterTypes.country)?.split(FilterUrlSplitter)?.includes(String(option.code))) || []
                  }
                  onChange={(_event, value) => {
                    const valuesForUrl = value.map((val) => val.code).join(FilterUrlSplitter);
                    updateSearchParams(searchParams, setSearchParams, { [FilterTypes.country]: valuesForUrl, [FilterTypes.page]: '1' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      fullWidth
                      placeholder={t('common.fields.country')}
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const { ...optionProps } = props;
                    return (
                      <li {...optionProps} key={option.code}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlank fontSize="small" />}
                          checkedIcon={<CheckBox fontSize="small" />}
                          sx={{ mr: 1 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    );
                  }}
                />
              </Box>

              <Box minWidth={BoxMinWidth}>
                <Autocomplete
                  fullWidth
                  disableCloseOnSelect
                  multiple
                  limitTags={1}
                  size="small"
                  options={ModStatus || []}
                  getOptionLabel={(option) => option}
                  value={
                    ModStatus?.filter((option) => searchParams
                      .get(FilterTypes.status)?.split(FilterUrlSplitter)?.includes(String(option))) || []
                  }
                  onChange={(_event, value) => {
                    const valuesForUrl = value.map((val) => val).join(FilterUrlSplitter);
                    updateSearchParams(searchParams, setSearchParams, { [FilterTypes.status]: valuesForUrl, [FilterTypes.page]: '1' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      fullWidth
                      placeholder={t('common.fields.status')}
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const { ...optionProps } = props;
                    return (
                      <li {...optionProps} key={option}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlank fontSize="small" />}
                          checkedIcon={<CheckBox fontSize="small" />}
                          sx={{ mr: 1 }}
                          checked={selected}
                        />
                        {t(`hotels.contentManager.filters.status.${option}`)}
                      </li>
                    );
                  }}
                />
              </Box>

              <Box minWidth={BoxMinWidth}>
                <MultiTextSelect type={FilterTypes.name} />
              </Box>

              <Box minWidth={BoxMinWidth}>
                <MultiTextSelect type={FilterTypes.address} />
              </Box>

              <Box minWidth={BoxMinWidth}>
                <MultiTextSelect type={FilterTypes.city} />
              </Box>

              <Box minWidth={BoxMinWidth}>
                <MultiTextSelect type={FilterTypes.code} />
              </Box>
              <Box minWidth={BoxMinWidth}>
                <MultiTextSelect type={FilterTypes.hotelId} />
              </Box>
              <Box minWidth={BoxMinWidth}>
                <MultiTextSelect type={FilterTypes.modParentId} />
              </Box>
            </Box>

            <StyledBoxFooter>
              <Button
                onClick={onClearFilters}
                size="small"
                color="error"
                variant="text"
                sx={{ minWidth: 100 }}
              >
                {t('filters.clearAll')}
              </Button>
            </StyledBoxFooter>
          </>
        ) 
      }
    </Box>
  );
};
